import { Box } from '@razorpay/blade/components';
import api from 'api';
import {
  WizardError,
  WizardHeader,
  WizardLoader,
  WizardProgressView,
  WizardWrapper,
} from 'components/WizardViews/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { startOfMonth } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import { useSearchParams } from 'hooks/useSearchParams';
import React from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import WizardPathToStepController from '../components/WizardPathToStepController';
import ReviewStep from './ReviewStep';
import SalaryDetailsStep from './SalaryDetailsStep';
import { revisePayRouteToStepMap, revisePaySteps, ReviseSalaryFormData } from './constants';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import { useListSalaryStructures } from 'components/SalaryStructures/hooks';
import { generateUserId } from 'utils/commons';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

const RevisePayWizard = () => {
  const history = useHistory();

  const { setFormData, formData, setFieldValue } = useFormWithProvider<ReviseSalaryFormData>();

  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;

  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();

  const payrollSetupQuery = useQuery({
    queryFn: () => api.userQueries.payrollGetSetup(),
    onSuccess: () => ctcQuery.refetch(),
  });

  const ctcQuery = useQuery({
    queryKey: ['getSalaryDetails', userId],
    queryFn: () =>
      api.userQueries.getSalaryDetails({
        userId,
      }),
    enabled: false,
    onSuccess: (data) => {
      if (!data.payrollGetSalaryDetails) return;
      const payrollDetails = data.payrollGetSalaryDetails.data;
      const payrollSetupInfo = payrollSetupQuery.data!.payrollGetSetup.data;

      setFormData({
        ...formData,
        currentAnnualCtc: payrollDetails.annualSalary,
        revisedCompensation: String(payrollDetails.annualSalary),
        currentCtcEffectiveFrom: payrollDetails.effectiveFromDate,
        payrollSetupInfo,
        effectiveFrom: startOfMonth(new Date()),
      });
    },
  });

  const {
    data: salaryStructureData,
    isFetching,
    error: listSalaryStructureError,
  } = useListSalaryStructures({
    enabled: isM3S1Enabled,
    queryKey: [salaryStructureQueries.getSalaryStructuresList],
    filters: 'SALARY_STRUCTURE_STATUS_ACTIVE',
  });

  const { isFetching: isFetchingSalaryStructuresForUsers, error: salaryStructuresForUsersError } =
    useQuery({
      queryKey: [salaryStructureQueries.getSalaryStructuresForUsers, userId],
      queryFn: async () => {
        const result = await api.salaryStructures.queries.getSalaryStructuresForUsers({
          input: {
            userIds: [generateUserId(userId)],
          },
        });

        return result.payrollGetSalaryStructuresForUsers.data;
      },
      onSuccess: (data) => {
        const { salaryStructureUsers } = data;
        if (salaryStructureUsers?.length) {
          const salaryStructure = salaryStructureUsers[0].salaryStructure;

          setFieldValue('salaryStructureId', [salaryStructure.id]);
        } else {
          const defaultSalaryStructure = salaryStructureData?.find(
            (salaryStructure) => salaryStructure.type === 'SALARY_STRUCTURE_TYPE_DEFAULT',
          )?.id;

          setFieldValue('salaryStructureId', [defaultSalaryStructure]);
        }
      },
      enabled: !isFetching && !!userId && isM3S1Enabled,
    });

  const handleCloseClick = () => {
    history.goBack();
  };

  if (
    ctcQuery.isFetching ||
    payrollSetupQuery.isFetching ||
    !ctcQuery.data ||
    isFetching ||
    isFetchingSalaryStructuresForUsers
  ) {
    return <WizardLoader />;
  }

  const error =
    ctcQuery.error ||
    payrollSetupQuery.error ||
    listSalaryStructureError ||
    salaryStructuresForUsersError;

  const payrollDetails = ctcQuery.data.payrollGetSalaryDetails.data;
  const name = payrollDetails.name;
  const firstName = name?.split(' ')[0];

  return (
    <WizardInfoProvider initialStep={0} totalSteps={2}>
      <WizardPathToStepController routeStepMap={revisePayRouteToStepMap} />
      <WizardWrapper>
        <WizardHeader
          heading="Revise compensation for"
          subHeading={firstName ?? 'Employee'}
          onCloseClick={handleCloseClick}>
          <WizardProgressView
            steps={[
              { label: 'Salary Details', position: revisePaySteps.step1 },
              { label: 'Review', position: revisePaySteps.step2 },
            ]}
          />
        </WizardHeader>
        <Box flex={1} overflowY="auto" height="100%">
          <WizardError onRetry={payrollSetupQuery.refetch} error={error}>
            <Wizard>
              <WizardStep
                step={revisePaySteps.step1}
                component={() => <SalaryDetailsStep salaryStructureData={salaryStructureData} />}
              />
              <WizardStep
                step={revisePaySteps.step2}
                component={() => (
                  <ReviewStep
                    salaryStructure={salaryStructureData?.find(
                      (salaryStructure) => salaryStructure.id === formData?.salaryStructureId?.[0],
                    )}
                  />
                )}
              />
            </Wizard>
          </WizardError>
        </Box>
      </WizardWrapper>
    </WizardInfoProvider>
  );
};

export default RevisePayWizard;
